import { type UseNavigateResult } from "@tanstack/react-router";
import { type Cache } from "@urql/exchange-graphcache";
import { Howl } from "howler";

import IncidentsStartedSfx from "@/assets/sounds/guardme-started.mp3";
import { type DocumentType } from "@/gql";
import { GraphqlIncidentsUpdatesEvent } from "@/gql/graphql";
import { toast } from "@/lib/toast";

import type { INCIDENTS_UPDATES_SUBSCRIPTION } from "../graphql/incidents-updates-subscription";

export function incidentsUpdatesResolver(navigate: UseNavigateResult<string>) {
  return (
    result: DocumentType<typeof INCIDENTS_UPDATES_SUBSCRIPTION>,
    _args: never,
    cache: Cache,
  ) => {
    if (!result?.incidentsUpdates?.incident) {
      // no incidents on object, do nothing
      return;
    }

    if (!result.incidentsUpdates.incident.organization) {
      // unknown organization
      return;
    }

    const event = result.incidentsUpdates.event ?? "updated";
    const incidentId = result.incidentsUpdates.incident.id;

    if (event === GraphqlIncidentsUpdatesEvent.Created) {
      const sound = new Howl({
        src: IncidentsStartedSfx,
      });

      const soundId = sound.play();
      const organizationId = result.incidentsUpdates.incident.organizationId;

      toast.info(`New Incident (${result.incidentsUpdates.incident.shortId})`, {
        description: result.incidentsUpdates.incident.subject,
        action: {
          label: "View",
          onClick: () => {
            navigate({
              params: {
                orgId: organizationId,
                id: incidentId,
              },
              to: "/$orgId/incidents/$id",
            });
          },
        },
      });

      setTimeout(() => {
        sound.stop(soundId);
      }, 3_000);

      // invalidate incident query results to cause refetch with new data
      for (const field of cache
        .inspectFields("Query")
        .filter((query) => query.fieldName === "incidents")) {
        cache.invalidate("Query", "incidents", field.arguments);
      }
    }
  };
}
